import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import { canSwitchBranch as canSwitchBranchPermission, canAddInventoryItem } from '../../lib/permissions';
import { AddItemModal } from './InventoryModals';
import AssignMissingSkusSheet from '../../components/inventory/AssignMissingSkusSheet';
import { categoryLabel } from '../../lib/categoryLabel';
import { getStockStatus } from '../../lib/stockStatus';
import {
  IconSearch,
  IconChevronDown,
  IconPhoto,
  IconPhotoOff,
  IconPackage,
  IconShoppingCartPlus,
} from '@tabler/icons-react';

const BRANCHES = [
  { id: 1, name: 'Dallas' },
  { id: 2, name: 'Houston' },
  { id: 3, name: 'Austin' },
  { id: 4, name: 'San Antonio' },
];

const ROWS_OPTIONS = [20, 50, 100];
const MOBILE_PAGE_SIZE = 20;

function getStockForBranch(item, branchId) {
  const rows = Array.isArray(item?.inventory_stock)
    ? item.inventory_stock
    : item?.inventory_stock ? [item.inventory_stock] : [];
  return rows.find(s => s.branch_id === branchId) || null;
}

// #357 — this file's local getStatus() tested the reorder point before the
// quantity, which is why the header read "OUT OF STOCK: 0" while the table
// below it listed 46 items at quantity 0. The rule now lives in one place:
// src/lib/stockStatus.js.

const STATUS_STYLES = {
  OK:  { color: '#1D9E75', bg: '#E8F5E9', dot: '#1D9E75' },
  // #658: the TEXT is #9A5F00, 4.77 on this pill; the dot is a graphic and keeps #BA7517.
  Low: { color: '#9A5F00', bg: '#FFF3E0', dot: '#BA7517' },
  Out: { color: '#A32D2D', bg: '#FFEBEE', dot: '#A32D2D' },
};

function StatusCell({ qty, reorderPoint }) {
  const status = getStockStatus(qty, reorderPoint);
  if (!status) return <span style={{ color: '#C0C0C0', fontSize: 12 }}>—</span>;
  const s = STATUS_STYLES[status];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot, display: 'inline-block', flexShrink: 0 }} />
      <span style={{ fontSize: 11, fontWeight: 700, color: s.color }}>{status}</span>
    </span>
  );
}

function Thumbnail({ imageUrl }) {
  return (
    <div style={{ width: 36, height: 36, borderRadius: 8, background: '#F0F0F0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {imageUrl
        ? <IconPhoto size={15} color="#39D353" />
        : <IconPhotoOff size={15} color="#C0C0C0" />
      }
    </div>
  );
}

// 74s. Resolution moved to src/lib/categoryLabel.js, which carries the full note on why
// the nested self-embed this used to read never returned a parent. #260's requirement
// still holds and is why the em dash is applied here rather than inside the helper:
// both branches must return the same shape, because returning a bare string for the
// uncategorised case made CategoryPill's `label.sub` guard resolve to
// String.prototype.sub - a real function, therefore truthy.
function getCategoryLabel(cat, categories) {
  const { parent, sub } = categoryLabel(cat, categories);
  return { parent: parent || '—', sub: sub || null };
}

function CategoryPill({ cat, categories }) {
  const label = getCategoryLabel(cat, categories);
  const colors = {
    'Stakes':           { bg: '#E8F5E9', color: '#1D9E75' },
    'Frames':           { bg: '#EEF2FF', color: '#3C3489' },
    'Sheeted Materials':{ bg: '#F0F0F0', color: '#5C5C5C' },
  };
  const key = label.parent;
  const style = colors[key] || { bg: '#F0F0F0', color: '#5C5C5C' };
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: style.bg, color: style.color }}>
        {key}
      </span>
      {label.sub && (
        <span style={{ fontSize: 10, color: '#9A9A9A', paddingLeft: 2 }}>{label.sub}</span>
      )}
    </span>
  );
}

function SortIcon({ field, currentField, currentDir }) {
  if (currentField !== field) return <span style={{ color: '#D0D0D0', fontSize: 10, marginLeft: 3 }}>↕</span>;
  return <span style={{ color: '#39D353', fontSize: 10, marginLeft: 3 }}>{currentDir === 'asc' ? '↑' : '↓'}</span>;
}

export default function InventoryCatalogPage({ setActive, setSelectedItemId }) {
  // labelPrintingEnabled -- #199 part 3: with QR label printing off, neither
  // Labels button is offered (approved drawing C).
  const { profile, isAdmin, isManager, isExecutive, isTeamMember, labelPrintingEnabled } = useAuth();
  const isMobile = window.innerWidth <= 768;
  const canSwitchBranch = canSwitchBranchPermission({ isAdmin, isManager, isExecutive });
  // #448 — passes isExecutive because canGovernCatalogItem reads it. Omitting it
  // hands the predicate `undefined`, which is falsy, and the widening silently
  // would not reach executives (locked rule 8).
  // #422/78t -- `profile` added. canGovernCatalogItem now reads
  // catalog_actions_enabled and manager_scope, so omitting profile hands it
  // undefined and every BRANCH MANAGER loses the button (locked rule 8).
  const canAddItem = canAddInventoryItem({ isAdmin, isManager, isExecutive, profile });

  const [branchId, setBranchId] = useState(null);
  const [showBranchPicker, setShowBranchPicker] = useState(false);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const [sortField, setSortField] = useState('qty');
  const [sortDir, setSortDir] = useState('asc');

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [mobileCount, setMobileCount] = useState(MOBILE_PAGE_SIZE);

  const [showAddItem, setShowAddItem] = useState(false);
  // #471, S15. The one-time backfill for items created before Stede could write
  // a SKU. Deliberately NOT a sidebar entry: it runs once, by one person, so a
  // permanent nav item would be clutter forever. It is reached from here, which
  // is also the gate's own breadcrumb - Inventory > Catalog > Assign missing SKUs.
  const [showAssignSkus, setShowAssignSkus] = useState(false);

  useEffect(() => {
    if (profile?.branch_id) setBranchId(profile.branch_id);
  }, [profile]);

  const branchName = BRANCHES.find(b => b.id === branchId)?.name || 'Loading...';

  const load = useCallback(async () => {
    if (!branchId) return;
    setLoading(true);

    const { data, error } = await supabase
      .from('inventory_items')
      .select(`
        id, name, sku, image_url, is_active, uom,
        inventory_categories ( id, name, parent_id ),
        inventory_vendors ( id, name ),
        inventory_stock (
          id, qty_on_hand, reorder_point, reorder_qty, location_note, branch_id
        )
      `);

    const branchFiltered = (data || []).filter(item =>
      item.inventory_stock?.some(s => s.branch_id === branchId)
    );
    if (!error) setItems(branchFiltered);

    const { data: cats } = await supabase
      .from('inventory_categories')
      // `code` is #471's category code, and the Add-item form cannot propose a
      // SKU without it. Named explicitly rather than select('*') to match this
      // file's existing style - which is exactly why adding the column to the
      // database was not enough on its own.
      .select('id, name, parent_id, is_active, code')
      .order('name');
    setCategories(cats || []);

    const { data: vens } = await supabase
      .from('inventory_vendors')
      .select('id, name')
      .eq('is_active', true)
      .order('name');
    setVendors(vens || []);

    setLoading(false);
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const filtered = items.filter(row => {
    if (!row) return false;
    if (!showInactive && !row.is_active) return false;
    const stock = getStockForBranch(row, branchId);
    if (!stock) return false;
    if (filterCategory && row.inventory_categories?.id !== parseInt(filterCategory) && row.inventory_categories?.parent_id !== parseInt(filterCategory)) return false;
    if (search) {
      const q = search.toLowerCase();
      const hay = [row.name, row.sku, row.inventory_categories?.name, row.inventory_vendors?.name].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const as = getStockForBranch(a, branchId);
    const bs = getStockForBranch(b, branchId);
    let av, bv;
    if (sortField === 'qty') { av = as?.qty_on_hand ?? 0; bv = bs?.qty_on_hand ?? 0; }
    else if (sortField === 'name') { av = a.name?.toLowerCase() || ''; bv = b.name?.toLowerCase() || ''; }
    else if (sortField === 'status') {
      const order = { Out: 0, Low: 1, OK: 2, null: 3 };
      av = order[getStockStatus(as?.qty_on_hand, as?.reorder_point)] ?? 3;
      bv = order[getStockStatus(bs?.qty_on_hand, bs?.reorder_point)] ?? 3;
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // #471, S15. Counted from the whole loaded catalog rather than from
  // `filtered`, deliberately: tying it to the search box would make the button
  // vanish the moment somebody typed, which reads as the control disappearing
  // rather than as the count being scoped. Blank is stored as NULL, and an
  // empty string is treated the same way here - the distinction matters to the
  // unique index, not to the person reading the number.
  const missingSkuCount = items.filter(i => !i.sku || !String(i.sku).trim()).length;

  const stats = {
    total: filtered.filter(r => r.is_active).length,
    ok:    filtered.filter(r => { const s = getStockForBranch(r, branchId); return r.is_active && getStockStatus(s?.qty_on_hand, s?.reorder_point) === 'OK'; }).length,
    low:   filtered.filter(r => { const s = getStockForBranch(r, branchId); return r.is_active && getStockStatus(s?.qty_on_hand, s?.reorder_point) === 'Low'; }).length,
    out:   filtered.filter(r => { const s = getStockForBranch(r, branchId); return r.is_active && getStockStatus(s?.qty_on_hand, s?.reorder_point) === 'Out'; }).length,
    // #376, Session 78n — the fourth bucket, and the reason the other three did
    // not add up. Counted from the live Dallas header at 78g rather than
    // estimated: `50 TOTAL · 4 IN STOCK · 0 LOW · 39 OUT` summed to 43 of 50.
    // The missing 7 hold stock with no reorder point, so getStockStatus()
    // returns null for them and they fell into no tile at all. Eleven Dallas
    // items held stock and the header said four.
    //
    // The null is CORRECT and is deliberately not folded into OK. 'OK' is a
    // positive claim that stock is above its threshold, and asserting it for an
    // item that has no threshold would be inventing a fact — see stockStatus.js.
    // The defect was always the header, never the predicate.
    //
    // A fourth tile rather than redefining "In stock" as qty > 0, per the issue:
    // it makes the missing master data VISIBLE instead of hiding it, and the
    // absent reorder points are the real problem underneath. Every new item is
    // still born into this bucket because InventoryModals.js hardcodes
    // reorder_point: null — that is #357's create-flow half and stays at Oct 1.
    // This tile is what makes that backlog countable in the meantime.
    //
    // Tested on qty_on_hand directly rather than on a null from getStockStatus,
    // because that function returns null for TWO different situations: this one,
    // and an item with no stock row at this branch at all. "Not stocked here" is
    // not "stocked here with no threshold", and stockStatus.js already refuses
    // to conflate them.
    noReorderPoint: filtered.filter(r => {
      const s = getStockForBranch(r, branchId);
      return r.is_active
        && Number(s?.qty_on_hand) > 0
        && (s?.reorder_point === null || s?.reorder_point === undefined);
    }).length,
  };

  function toggleSort(field) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  }

  const activeFilterCount = [filterCategory].filter(Boolean).length;
  const topCatOptions = categories.filter(c => !c.parent_id);

  const paged = sorted.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const mobileVisible = sorted.slice(0, mobileCount);

  function handleRowClick(row) {
    if (setSelectedItemId) setSelectedItemId(row.id);
    if (setActive) setActive('inv_detail');
  }

  // ── MOBILE ──────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ background: '#F5F5F3', minHeight: '100vh', paddingBottom: 80 }}>

        <div style={{ background: 'white', padding: '12px 14px 10px', borderBottom: '1px solid #E8E8E8', position: 'sticky', top: 'var(--topbar-height)', zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0F0F0F' }}>Catalog</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={() => setActive?.('inv_request_materials')}
                style={{ padding: '6px 10px', background: '#FFF8EC', color: '#EF9F27', border: '1px solid #EF9F27', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                <IconShoppingCartPlus size={13} color="#EF9F27" /> Request
              </button>
              {labelPrintingEnabled !== false && (
              <button onClick={() => setActive('inv_labels')}
                style={{ padding: '6px 10px', background: '#e8f8f2', color: '#085041', border: '1px solid #9FE1CB', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                🖨 Labels
              </button>
              )}
              {canAddItem && (
                <button onClick={() => setShowAddItem(true)}
                  style={{ padding: '6px 12px', background: '#39D353', color: '#0F0F0F', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  + Add item
                </button>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 0, background: '#F5F5F3', borderRadius: 8, overflow: 'hidden', border: '1px solid #E8E8E8', marginBottom: 8 }}>
            {[
              { label: 'Items', val: stats.total, color: '#0F0F0F' },
              { label: 'OK',    val: stats.ok,    color: '#1D9E75' },
              { label: 'Low',   val: stats.low,   color: '#9A5F00' },
              { label: 'Out',   val: stats.out,   color: '#A32D2D' },
              // #376 -- shorter label than desktop's for the same reason 'OK'
              // and 'Out' are shorter here: four characters of room, not a
              // different meaning. Grey rather than a status colour, because it
              // reports missing master data rather than a stock condition.
              { label: 'No reorder', val: stats.noReorderPoint, color: '#5C5C5C' },
            ].map((s, i, arr) => (
              <div key={i} style={{ flex: 1, padding: '6px 4px', textAlign: 'center', borderRight: i < arr.length - 1 ? '1px solid #E8E8E8' : 'none' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: 9, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: '#F0F0F0', borderRadius: 8, padding: '7px 10px', marginBottom: 6 }}>
            <IconSearch size={13} color="#9A9A9A" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setMobileCount(MOBILE_PAGE_SIZE); }}
              placeholder="Search items..."
              style={{ border: 'none', background: 'none', outline: 'none', color: '#0F0F0F', flex: 1 }}
            />
          </div>

          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <select
                value={filterCategory}
                onChange={e => { setFilterCategory(e.target.value); setMobileCount(MOBILE_PAGE_SIZE); }}
                style={{ width: '100%', height: 32, border: filterCategory ? '1px solid #39D353' : '1px solid #E0E0E0', borderRadius: 8, padding: '0 8px', color: '#5C5C5C', background: filterCategory ? '#F0FFF4' : 'white', appearance: 'none' }}
              >
                <option value="">All categories</option>
                {topCatOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {filterCategory && <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: '#39D353', color: '#0F0F0F', borderRadius: '50%', width: 14, height: 14, fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</span>}
            </div>
            {canSwitchBranch && (
              <select
                value={branchId || ''}
                onChange={e => { setBranchId(parseInt(e.target.value)); setMobileCount(MOBILE_PAGE_SIZE); }}
                style={{ width: 90, height: 32, border: '1px solid #E0E0E0', borderRadius: 8, padding: '0 8px', color: '#5C5C5C', background: 'white', appearance: 'none' }}
              >
                {BRANCHES.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            )}
          </div>
        </div>

        <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 5 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9A9A9A', textTransform: 'uppercase', letterSpacing: '0.6px', padding: '2px 2px 4px' }}>
            Sorted by {sortField === 'qty' ? 'qty · low first' : sortField} · {sorted.length} items
          </div>

          {loading ? (
            <div style={{ padding: '32px', textAlign: 'center', color: '#9A9A9A', fontSize: 13 }}>Loading...</div>
          ) : mobileVisible.length === 0 ? (
            <div style={{ background: 'white', borderRadius: 10, padding: '24px 16px', border: '1px solid #E8E8E8', textAlign: 'center' }}>
              <IconPackage size={28} color="#D0D0D0" style={{ marginBottom: 8 }} />
              <div style={{ fontSize: 13, fontWeight: 600, color: '#9A9A9A' }}>No items found</div>
              <div style={{ fontSize: 11, color: '#C0C0C0', marginTop: 4 }}>Try adjusting your search or filters</div>
            </div>
          ) : mobileVisible.map(row => {
            const item = row;
            const stock = getStockForBranch(row, branchId);
            const status = getStockStatus(stock?.qty_on_hand, stock?.reorder_point);
            const s = status ? STATUS_STYLES[status] : null;
            const catLabel = getCategoryLabel(item?.inventory_categories, categories);
            const catName = catLabel.parent;
            return (
              <button
                key={row.id}
                onClick={() => handleRowClick(row)}
                style={{ background: item?.is_active ? 'white' : '#FAFAFA', borderRadius: 10, border: '1px solid #E8E8E8', padding: '9px 10px', display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', width: '100%', opacity: item?.is_active ? 1 : 0.5, cursor: 'pointer' }}
              >
                <Thumbnail imageUrl={item?.image_url} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#0F0F0F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item?.name}
                    {!item?.is_active && <span style={{ marginLeft: 5, padding: '0 5px', borderRadius: 5, fontSize: 9, background: '#F0F0F0', color: '#9A9A9A' }}>Inactive</span>}
                  </div>
                  <div style={{ fontSize: 10, color: '#7A7A7A', marginTop: 1 }}>{catName} · {item?.uom}</div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: s ? s.color : '#0F0F0F' }}>{stock?.qty_on_hand ?? 0}</div>
                  {s && <span style={{ display: 'inline-block', padding: '1px 5px', borderRadius: 6, fontSize: 9, fontWeight: 700, background: s.bg, color: s.color }}>{status}</span>}
                </div>
              </button>
            );
          })}

          {mobileVisible.length < sorted.length && (
            <button
              onClick={() => setMobileCount(c => c + MOBILE_PAGE_SIZE)}
              style={{ display: 'block', width: '100%', padding: 11, borderRadius: 10, border: '1px solid #E0E0E0', background: 'white', fontSize: 12, fontWeight: 700, color: '#5C5C5C', cursor: 'pointer', marginTop: 2 }}
            >
              Load more ({sorted.length - mobileVisible.length} remaining)
            </button>
          )}
        </div>

        {showAddItem && (
          <AddItemModal
            categories={categories}
            vendors={vendors}
            onClose={() => setShowAddItem(false)}
            onSaved={async () => { setShowAddItem(false); await load(); }}
          />
        )}
      </div>
    );
  }

  // ── DESKTOP ──────────────────────────────────────────────
  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="page-title">Inventory catalog</div>
          <div className="page-sub">{branchName} · {stats.total} active item{stats.total !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setActive?.('inv_request_materials')}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: '#FFF8EC', color: '#EF9F27', border: '1px solid #EF9F27', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            <IconShoppingCartPlus size={15} color="#EF9F27" /> Request
          </button>
          {labelPrintingEnabled !== false && (
          <button className="btn btn-ghost" onClick={() => setActive('inv_labels')} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            🖨 Print labels
          </button>
          )}
          {/* #471, S15. Appears only while there is something to assign, so it
              retires itself the moment the backfill is done rather than sitting
              there forever offering to do nothing. Desktop only, per gate
              callout J - the mobile header above deliberately has no equivalent.
              Gated on canAddInventoryItem because #448 settled that ONE rule
              governs every door that creates or changes a catalog item, and
              adding a narrower rule here would repeat what #448 fixed. */}
          {canAddItem && missingSkuCount > 0 && (
            <button className="btn btn-ghost" onClick={() => setShowAssignSkus(true)}
              title={`${missingSkuCount} ${missingSkuCount === 1 ? 'item has' : 'items have'} no SKU`}>
              Assign missing SKUs ({missingSkuCount})
            </button>
          )}
          {canAddItem && (
            <button className="btn btn-primary" onClick={() => setShowAddItem(true)}>+ Add item</button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: '1rem', background: 'white', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', overflow: 'hidden' }}>
        {[
          { label: 'Total items', val: stats.total, color: 'var(--black)'  },
          { label: 'In stock',    val: stats.ok,    color: '#1D9E75'       },
          { label: 'Low stock',   val: stats.low,   color: '#9A5F00'       },
          { label: 'Out of stock',val: stats.out,   color: '#A32D2D'       },
          { label: 'No reorder point', val: stats.noReorderPoint, color: '#5C5C5C' },
        ].map((s, i, arr) => (
          <div key={i} style={{ flex: 1, padding: '10px 16px', textAlign: 'center', borderRight: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.color, fontFamily: 'var(--font-head)' }}>{s.val}</div>
            <div style={{ fontSize: 11, color: 'var(--gray-mid)', marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.label}</div>
          </div>
        ))}
      </div>

      <div className="toolbar">
        <div style={{ position: 'relative', flex: 1, minWidth: 160 }}>
          <IconSearch size={14} color="#9A9A9A" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
          <input
            className="search-input"
            placeholder="Search items, SKU, vendor..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ paddingLeft: 32, width: '100%' }}
          />
        </div>

        <div style={{ position: 'relative' }}>
          <select
            className="filter-select"
            value={filterCategory}
            onChange={e => { setFilterCategory(e.target.value); setPage(1); }}
            style={{ borderColor: filterCategory ? '#39D353' : undefined, background: filterCategory ? '#F0FFF4' : 'white', paddingRight: filterCategory ? 28 : undefined }}
          >
            <option value="">All categories</option>
            {topCatOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {filterCategory && (
            <span style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: '#39D353', color: '#0F0F0F', borderRadius: '50%', width: 16, height: 16, fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</span>
          )}
        </div>

        {canSwitchBranch && (
          <select
            className="filter-select"
            value={branchId || ''}
            onChange={e => { setBranchId(parseInt(e.target.value)); setPage(1); }}
          >
            {BRANCHES.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--gray-mid)', cursor: 'pointer', userSelect: 'none' }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={e => { setShowInactive(e.target.checked); setPage(1); }}
            style={{ accentColor: 'var(--green-dk)' }}
          />
          Show inactive
        </label>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? (
            <div className="loading"><div className="spinner" />Loading...</div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th style={{ width: 46 }}></th>
                  <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                    Item name <SortIcon field="name" currentField={sortField} currentDir={sortDir} />
                  </th>
                  <th>Category</th>
                  <th style={{ width: 70 }}>UOM</th>
                  <th style={{ cursor: 'pointer', width: 120 }} onClick={() => toggleSort('qty')}>
                    Qty on hand <SortIcon field="qty" currentField={sortField} currentDir={sortDir} />
                  </th>
                  <th style={{ cursor: 'pointer', width: 100 }} onClick={() => toggleSort('status')}>
                    Status <SortIcon field="status" currentField={sortField} currentDir={sortDir} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr><td colSpan={6}>
                    <div className="empty-state">
                      <div className="empty-state-icon">📦</div>
                      <div className="empty-state-title">No items found</div>
                      <div className="empty-state-sub">Try adjusting your search or filters.</div>
                    </div>
                  </td></tr>
                ) : paged.map(row => {
                  const item = row;
                    const stock = getStockForBranch(row, branchId);
                    return (
                    <tr
                      key={row.id}
                      onClick={() => handleRowClick(row)}
                      style={{ cursor: 'pointer', opacity: item?.is_active ? 1 : 0.45 }}
                    >
                      <td><Thumbnail imageUrl={item?.image_url} /></td>
                      <td>
                        <div style={{ fontWeight: 700, fontSize: 13 }}>
                          {item?.name}
                          {!item?.is_active && <span style={{ marginLeft: 6, padding: '1px 6px', borderRadius: 6, fontSize: 10, background: '#F0F0F0', color: '#9A9A9A' }}>Inactive</span>}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--gray-lt)' }}>{item?.sku}</div>
                      </td>
                      <td><CategoryPill cat={item?.inventory_categories} categories={categories} /></td>
                      <td style={{ color: 'var(--gray-mid)', fontSize: 12 }}>{item?.uom}</td>
                      <td style={{ fontWeight: 700 }}>{stock?.qty_on_hand ?? 0}</td>
                      <td><StatusCell qty={stock?.qty_on_hand ?? 0} reorderPoint={stock?.reorder_point} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderTop: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, color: 'var(--gray-mid)', whiteSpace: 'nowrap' }}>Rows per page</span>
            <select
              value={rowsPerPage}
              onChange={e => { setRowsPerPage(parseInt(e.target.value)); setPage(1); }}
              style={{ height: 28, border: '1px solid var(--border)', borderRadius: 6, padding: '0 8px', fontWeight: 600, color: 'var(--black)', background: 'white', cursor: 'pointer' }}
            >
              {ROWS_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>

          <span style={{ fontSize: 11, color: 'var(--gray-mid)' }}>
            {sorted.length === 0 ? '0 items' : `Showing ${(page - 1) * rowsPerPage + 1}–${Math.min(page * rowsPerPage, sorted.length)} of ${sorted.length} items`}
            {sortField && ` · sorted by ${sortField} ${sortDir === 'asc' ? '↑' : '↓'}`}
          </span>

          <div style={{ display: 'flex', gap: 4 }}>
            <button className="btn btn-ghost btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Prev</button>
            <button className="btn btn-ghost btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next →</button>
          </div>
        </div>
      </div>

      {showAddItem && (
        <AddItemModal
          categories={categories}
          vendors={vendors}
          onClose={() => setShowAddItem(false)}
          onSaved={async () => { setShowAddItem(false); await load(); }}
        />
      )}

      {showAssignSkus && (
        <AssignMissingSkusSheet
          onClose={() => setShowAssignSkus(false)}
          onAssigned={async () => { await load(); }}
        />
      )}
    </div>
  );
}